/**
 * The marketing Library's six kinds, as pure rules — Block 1.1 of the
 * marketing plan (plans/execution-plan-2026-09.md §2.3), E1.2.
 *
 * Every validator here answers "is this body a valid <kind>?" with a list of
 * problems the editor can show and the API can refuse on, and returns the
 * normalised fields the row is written from. Nothing here touches a database:
 * the checks that need one — does this pool exist, does that theme have an
 * approved version — live in the router, and the checks that need the
 * compiler — does this block compile at strict — live beside the renderer.
 *
 * Two shapes are defined here for the first time, because the schema left
 * them untyped (jsonb):
 *
 *   - `marketing_templates.layout` — the skeleton: MJML sections in order,
 *     some static, some a slot marker that assembly fills from a pool.
 *   - `segments.audience_query` — P12's small, explicit filter language over
 *     ActOS contacts joined to the spine `accounts`.
 *
 * And one decision the schema forced: `articles` carry no pool column, so an
 * article slot draws from every approved article — by tag fit, in Block 2 —
 * and its `pool_object_id` is null. A block slot names its pool.
 */

import { checkBlockSource, checkThemeTokens, type BlockProblem, type ThemeTokens } from '../mjml/index';
import type { EntitySource } from '../identity/index';
import { normaliseCountryCode } from '../geo/countries';
import { SIZE_BANDS } from '../prospecting/index';

export type { BlockProblem };

export const SLOT_KINDS = ['content_block', 'article'] as const;
export type SlotKind = (typeof SLOT_KINDS)[number];
/** What a pool holds — the same two words, so a slot's pool is checked against them. */
export const POOL_KINDS = SLOT_KINDS;

/**
 * R3 of the marketing redesign (plans/marketing-redesign.md): a block has a
 * TYPE, and the type is what a template placeholder draws from. Pools still
 * exist in the schema — `template_slots.pool_object_id` and the assembly
 * engine read them — but one is kept per type by the API, found by label,
 * and nobody names one. `custom` is the escape hatch for a block that is
 * none of the others; a placeholder of type custom draws from every custom
 * block, which is what "anything" means.
 */
export const BLOCK_TYPES = [
  { key: 'header', label: 'Header', hint: 'The top stripe: a kicker, a logo, a date line.' },
  { key: 'intro', label: 'Intro', hint: 'The opening: a greeting and the paragraph that earns the rest.' },
  { key: 'feature', label: 'Feature', hint: 'One thing worth knowing, with a heading.' },
  { key: 'cta', label: 'Call to action', hint: 'The button, and the sentence that justifies it.' },
  { key: 'article_list', label: 'Article list', hint: 'Links with a title and a line each.' },
  { key: 'footer', label: 'Footer', hint: 'Postal address, the unsubscribe link, the notices the law owes.' },
  { key: 'custom', label: 'Custom', hint: 'A block that is none of the above.' },
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number]['key'];

export function isBlockType(value: unknown): value is BlockType {
  return typeof value === 'string' && BLOCK_TYPES.some((t) => t.key === value);
}

/** The label a type's pool carries, and the word a person reads for it. */
export function blockTypeLabel(key: string): string {
  return BLOCK_TYPES.find((t) => t.key === key)?.label ?? key;
}

/** The sources a contact can carry; `contacts.primary_source` and `contact_sources.source`. */
export const CONTACT_SOURCES = [
  'crm',
  'amplemarket',
  'contactout',
  'manual',
  'import',
] as const satisfies readonly EntitySource[];

const SLUG = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const NAME_MAX = 120;
const SUMMARY_MAX = 1000;
const MAX_ITEMS_CAP = 10;

const at = (message: string, line = 0): BlockProblem => ({ line, message });

function asRecord(body: unknown): Record<string, unknown> | null {
  return body && typeof body === 'object' && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : null;
}

function checkName(value: unknown, field = 'name'): { name: string; problems: BlockProblem[] } {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return { name, problems: [at(`A ${field} is required.`)] };
  if (name.length > NAME_MAX) return { name, problems: [at(`A ${field} is at most ${NAME_MAX} characters.`)] };
  return { name, problems: [] };
}

function checkSlugs(value: unknown, field: string): { slugs: string[]; problems: BlockProblem[] } {
  if (value === undefined || value === null) return { slugs: [], problems: [] };
  if (!Array.isArray(value)) return { slugs: [], problems: [at(`${field} must be a list.`)] };
  const problems: BlockProblem[] = [];
  const slugs: string[] = [];
  for (const raw of value) {
    const slug = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (!SLUG.test(slug)) {
      problems.push(at(`${field}: "${String(raw)}" is not a tag — lower-case letters, digits, - and _, up to 40 characters.`));
    } else if (!slugs.includes(slug)) {
      slugs.push(slug);
    }
  }
  return { slugs, problems };
}

export type Parsed<F> = { ok: true; name: string; fields: F } | { ok: false; problems: BlockProblem[] };

function parsed<F>(name: string, fields: F, problems: BlockProblem[]): Parsed<F> {
  return problems.length > 0 ? { ok: false, problems } : { ok: true, name, fields };
}

// ---- Themes -----------------------------------------------------------------

export type ThemeFields = {
  name: string;
  tokens: ThemeTokens;
};

export function checkThemeInput(body: unknown): Parsed<ThemeFields> {
  const b = asRecord(body);
  if (!b) return { ok: false, problems: [at('A theme is an object with a name and tokens.')] };
  const { name, problems } = checkName(b.name);
  problems.push(...checkThemeTokens(b.tokens));
  return parsed(name, { name, tokens: (b.tokens ?? {}) as ThemeTokens }, problems);
}

// ---- Pools ------------------------------------------------------------------

export type PoolFields = {
  name: string;
  kind: SlotKind;
};

export function checkPoolInput(body: unknown): Parsed<PoolFields> {
  const b = asRecord(body);
  if (!b) return { ok: false, problems: [at('A pool is an object with a name and a kind.')] };
  const { name, problems } = checkName(b.name);
  const kind = b.kind;
  if (!(POOL_KINDS as readonly unknown[]).includes(kind)) {
    problems.push(at(`A pool holds one of: ${POOL_KINDS.join(', ')}.`));
  }
  return parsed(name, { name, kind: kind as SlotKind }, problems);
}

// ---- Blocks -----------------------------------------------------------------

export type BlockFields = {
  poolObjectId: number;
  /** A short category — intro, feature, proof, cta — for people; selection is by pool and tags. */
  kind: string;
  tags: string[];
  bodyMjml: string;
};

export function checkBlockInput(body: unknown): Parsed<BlockFields> {
  const b = asRecord(body);
  if (!b) return { ok: false, problems: [at('A block is an object with a name, a pool, a kind, tags and MJML.')] };
  const { name, problems } = checkName(b.name);

  const poolObjectId = Number(b.poolObjectId);
  if (!Number.isInteger(poolObjectId) || poolObjectId <= 0) {
    problems.push(at('A block belongs to a pool; poolObjectId is required.'));
  }

  const kind = typeof b.kind === 'string' ? b.kind.trim().toLowerCase() : '';
  if (!SLUG.test(kind)) {
    problems.push(at('A block has a kind — a short label such as intro, feature or cta.'));
  }

  const tags = checkSlugs(b.tags, 'tags');
  problems.push(...tags.problems);

  const bodyMjml = typeof b.bodyMjml === 'string' ? b.bodyMjml : '';
  if (!bodyMjml.trim()) {
    problems.push(at('A block is MJML — one or more mj-section elements — and this one is empty.'));
  } else {
    problems.push(...checkBlockSource(bodyMjml).problems);
  }

  return parsed(name, { poolObjectId, kind, tags: tags.slugs, bodyMjml }, problems);
}

// ---- Articles ---------------------------------------------------------------

export type ArticleFields = {
  url: string;
  title: string;
  summary: string | null;
  tags: string[];
  publishedAt: Date | null;
};

const HTTPS = /^https:\/\/\S+$/i;

export function checkArticleInput(body: unknown): Parsed<ArticleFields> {
  const b = asRecord(body);
  if (!b) return { ok: false, problems: [at('An article is an object with a title and an https: url.')] };
  const { name: title, problems } = checkName(b.title, 'title');

  const url = typeof b.url === 'string' ? b.url.trim() : '';
  if (!HTTPS.test(url)) problems.push(at('An article needs an https: url.'));

  const summary = typeof b.summary === 'string' && b.summary.trim() ? b.summary.trim() : null;
  if (summary && summary.length > SUMMARY_MAX) {
    problems.push(at(`A summary is at most ${SUMMARY_MAX} characters.`));
  }

  const tags = checkSlugs(b.tags, 'tags');
  problems.push(...tags.problems);

  let publishedAt: Date | null = null;
  if (b.publishedAt !== undefined && b.publishedAt !== null && b.publishedAt !== '') {
    const date = new Date(String(b.publishedAt));
    if (Number.isNaN(date.getTime())) problems.push(at('publishedAt is not a date.'));
    else publishedAt = date;
  }

  return parsed(title, { url, title, summary, tags: tags.slugs, publishedAt }, problems);
}

// ---- Templates --------------------------------------------------------------

export type LayoutSection = { type: 'slot'; position: number } | { type: 'static'; mjml: string };

/**
 * `marketing_templates.layout`. The skeleton is itself MJML sections in
 * order, with slot markers where assembly places pool content; static
 * sections obey the block allowlist, since they are sent as written.
 */
export interface TemplateLayout {
  sections: LayoutSection[];
}

export function checkTemplateLayout(value: unknown): { problems: BlockProblem[]; layout: TemplateLayout | null } {
  const l = asRecord(value);
  if (!l || !Array.isArray(l.sections)) {
    return { problems: [at('A layout is { sections: [...] } — slot markers and static MJML sections, in order.')], layout: null };
  }
  const problems: BlockProblem[] = [];
  const sections: LayoutSection[] = [];
  let nextSlot = 0;
  l.sections.forEach((raw, index) => {
    const s = asRecord(raw);
    if (s?.type === 'slot') {
      const position = Number(s.position);
      if (position !== nextSlot) {
        problems.push(
          at(`Section ${index + 1}: slot positions run 0, 1, 2… in order of appearance; expected ${nextSlot}, found ${String(s.position)}.`),
        );
      }
      nextSlot += 1;
      sections.push({ type: 'slot', position });
    } else if (s?.type === 'static') {
      const mjml = typeof s.mjml === 'string' ? s.mjml : '';
      if (!mjml.trim()) {
        problems.push(at(`Section ${index + 1}: a static section needs MJML.`));
      } else {
        for (const p of checkBlockSource(mjml).problems) {
          problems.push(at(`Section ${index + 1}, line ${p.line}: ${p.message}`));
        }
      }
      sections.push({ type: 'static', mjml });
    } else {
      problems.push(at(`Section ${index + 1}: type must be "slot" or "static".`));
    }
  });
  if (sections.length === 0) problems.push(at('A layout needs at least one section.'));
  return { problems, layout: problems.length > 0 ? null : { sections } };
}

export interface SlotInput {
  position: number;
  kind: SlotKind;
  minItems: number;
  maxItems: number;
  /** Required for a block slot; null for an article slot, which draws from every approved article. */
  poolObjectId: number | null;
}

export function slotPositions(layout: TemplateLayout): number[] {
  return layout.sections.flatMap((s) => (s.type === 'slot' ? [s.position] : []));
}

export function checkTemplateSlots(value: unknown, layout: TemplateLayout): { problems: BlockProblem[]; slots: SlotInput[] } {
  if (!Array.isArray(value)) return { problems: [at('slots must be a list, one per slot marker in the layout.')], slots: [] };
  const problems: BlockProblem[] = [];
  const wanted = slotPositions(layout);
  const seen = new Set<number>();
  const slots: SlotInput[] = [];

  for (const raw of value) {
    const s = asRecord(raw) ?? {};
    const position = Number(s.position);
    if (!wanted.includes(position)) {
      problems.push(at(`Slot ${String(s.position)}: the layout has no slot marker at that position.`));
      continue;
    }
    if (seen.has(position)) {
      problems.push(at(`Slot ${position} is listed twice.`));
      continue;
    }
    seen.add(position);

    const kind = s.kind as SlotKind;
    if (!(SLOT_KINDS as readonly unknown[]).includes(kind)) {
      problems.push(at(`Slot ${position}: kind must be one of ${SLOT_KINDS.join(', ')}.`));
    }
    const minItems = Number(s.minItems ?? 1);
    const maxItems = Number(s.maxItems ?? 1);
    if (!Number.isInteger(minItems) || !Number.isInteger(maxItems) || minItems < 0 || maxItems < 1 || minItems > maxItems) {
      problems.push(at(`Slot ${position}: 0 ≤ min ≤ max, and max ≥ 1.`));
    } else if (maxItems > MAX_ITEMS_CAP) {
      problems.push(at(`Slot ${position}: at most ${MAX_ITEMS_CAP} items — an email is not a feed.`));
    }
    const pool = s.poolObjectId === undefined || s.poolObjectId === null ? null : Number(s.poolObjectId);
    if (kind === 'content_block' && (pool === null || !Number.isInteger(pool) || pool <= 0)) {
      problems.push(at(`Slot ${position}: a block slot names the pool it draws from.`));
    }
    if (kind === 'article' && pool !== null) {
      problems.push(at(`Slot ${position}: an article slot draws from every approved article; it has no pool.`));
    }
    slots.push({ position, kind, minItems, maxItems, poolObjectId: kind === 'article' ? null : pool });
  }

  for (const position of wanted) {
    if (!seen.has(position)) problems.push(at(`The layout's slot ${position} has no slot definition.`));
  }
  slots.sort((a, b) => a.position - b.position);
  return { problems, slots };
}

export type TemplateFields = {
  name: string;
  layout: TemplateLayout;
  themeObjectId: number | null;
};

export function checkTemplateInput(body: unknown): Parsed<TemplateFields> & { slots?: SlotInput[] } {
  const b = asRecord(body);
  if (!b) return { ok: false, problems: [at('A template is an object with a name, a layout and slots.')] };
  const { name, problems } = checkName(b.name);
  const layout = checkTemplateLayout(b.layout);
  problems.push(...layout.problems);
  let slots: SlotInput[] = [];
  if (layout.layout) {
    const checked = checkTemplateSlots(b.slots ?? [], layout.layout);
    problems.push(...checked.problems);
    slots = checked.slots;
  }
  const theme = b.themeObjectId === undefined || b.themeObjectId === null ? null : Number(b.themeObjectId);
  if (theme !== null && (!Number.isInteger(theme) || theme <= 0)) problems.push(at('themeObjectId must name a theme.'));
  if (problems.length > 0) return { ok: false, problems };
  return { ok: true, name, fields: { name, layout: layout.layout!, themeObjectId: theme }, slots };
}

// ---- Segments — the audience language (P12) --------------------------------

export const AUDIENCE_FIELDS = ['list_id', 'country_code', 'industry', 'employee_band', 'title', 'source'] as const;
export type AudienceField = (typeof AUDIENCE_FIELDS)[number];

/** How long one industry value or one title keyword may be. */
const INDUSTRY_MAX = 120;
const TITLE_KEYWORD_MAX = 60;
const TITLE_KEYWORDS_MAX = 10;

/**
 * `segments.audience_query`. Every key optional; a present key is a non-empty
 * list and matches any of its values; keys combine with AND. `{}` matches
 * every contact — the exclusions still apply, and the count preview says
 * so. Evaluated against `contacts` joined to the spine `accounts`: industry,
 * band and open opportunity are the account's; country is the contact's
 * own where known, the account's otherwise; source is the contact's primary
 * source or any `contact_sources` row; `list_id` is membership of a
 * `contact_lists` row (an upload, a prospecting run); `title` is a set of
 * keywords, any of which the contact's title contains.
 *
 * Industry takes whatever value an account actually carries. The filter once
 * insisted on the vendors' LinkedIn taxonomy, but the CRM mirror stores
 * HubSpot's own strings verbatim, so the filter matched only vendor-enriched
 * accounts and silently missed every synced one. The picker offers the
 * values that exist, with counts; the check only bounds their length.
 */
export type AudienceQuery = Partial<Record<AudienceField, string[]>>;

export function checkAudienceQuery(value: unknown): { problems: BlockProblem[]; query: AudienceQuery | null } {
  const q = asRecord(value);
  if (!q) return { problems: [at(`An audience is an object of filters: ${AUDIENCE_FIELDS.join(', ')}.`)], query: null };
  const problems: BlockProblem[] = [];
  const query: AudienceQuery = {};
  for (const [key, raw] of Object.entries(q)) {
    if (!(AUDIENCE_FIELDS as readonly string[]).includes(key)) {
      problems.push(at(`"${key}" is not an audience filter. The filters are ${AUDIENCE_FIELDS.join(', ')}.`));
      continue;
    }
    if (!Array.isArray(raw) || raw.length === 0) {
      problems.push(at(`${key}: an empty list matches nobody — leave the filter out to match everyone.`));
      continue;
    }
    const values = [...new Set(raw.map((v) => String(v).trim()))].filter(Boolean);
    const field = key as AudienceField;
    if (field === 'country_code') {
      const codes = values.map((v) => normaliseCountryCode(v) ?? v.toUpperCase());
      const bad = codes.filter((v) => normaliseCountryCode(v) === null);
      if (bad.length) problems.push(at(`country_code: ${bad.join(', ')} — not a country.`));
      query.country_code = [...new Set(codes)].sort();
    } else if (field === 'industry') {
      const bad = values.filter((v) => v.length > INDUSTRY_MAX);
      if (bad.length) problems.push(at(`industry: a value longer than ${INDUSTRY_MAX} characters.`));
      query.industry = values.sort();
    } else if (field === 'employee_band') {
      const bad = values.filter((v) => !(SIZE_BANDS as readonly string[]).includes(v));
      if (bad.length) problems.push(at(`employee_band: ${bad.join(', ')} — the bands are ${SIZE_BANDS.join(', ')}.`));
      query.employee_band = values.sort();
    } else if (field === 'list_id') {
      const bad = values.filter((v) => !/^[1-9]\d*$/.test(v));
      if (bad.length) problems.push(at(`list_id: ${bad.join(', ')} — a list is named by its id.`));
      query.list_id = values.sort((a, b) => Number(a) - Number(b));
    } else if (field === 'title') {
      if (values.length > TITLE_KEYWORDS_MAX) problems.push(at(`title: at most ${TITLE_KEYWORDS_MAX} keywords.`));
      const bad = values.filter((v) => v.length > TITLE_KEYWORD_MAX);
      if (bad.length) problems.push(at(`title: a keyword longer than ${TITLE_KEYWORD_MAX} characters.`));
      query.title = values.sort();
    } else {
      const bad = values.filter((v) => !(CONTACT_SOURCES as readonly string[]).includes(v));
      if (bad.length) problems.push(at(`source: ${bad.join(', ')} — the sources are ${CONTACT_SOURCES.join(', ')}.`));
      query.source = values.sort();
    }
  }
  return { problems, query: problems.length > 0 ? null : query };
}

/** A size band as a person reads it: "51–200 people", "10,001+ people". */
export function bandLabel(band: string): string {
  const m = band.match(/^(\d+)-(\d+)$/);
  const n = (s: string) => Number(s).toLocaleString('en-US');
  if (m) return `${n(m[1]!)}–${n(m[2]!)} people`;
  if (/^\d+\+$/.test(band)) return `${n(band.slice(0, -1))}+ people`;
  return band;
}

export type SegmentFields = {
  name: string;
  audienceQuery: AudienceQuery;
};

export function checkSegmentInput(body: unknown): Parsed<SegmentFields> {
  const b = asRecord(body);
  if (!b) return { ok: false, problems: [at('A segment is an object with a name and an audienceQuery.')] };
  const { name, problems } = checkName(b.name);
  const query = checkAudienceQuery(b.audienceQuery ?? {});
  problems.push(...query.problems);
  return parsed(name, { name, audienceQuery: query.query ?? {} }, problems);
}

// ---- Campaign positions (R5, R6) --------------------------------------------

export const POSITION_MODES = ['fixed', 'varies'] as const;
export type PositionMode = (typeof POSITION_MODES)[number];

/**
 * How an email fills one placeholder: one block for everyone, or a set the
 * engine ranks per recipient. `candidateObjectIds` empty with mode `varies`
 * means every approved block of the type — the engine's original behaviour.
 */
export interface PositionChoice {
  position: number;
  mode: PositionMode;
  blockObjectId: number | null;
  candidateObjectIds: number[];
}

export function checkCampaignPositions(value: unknown): { problems: BlockProblem[]; positions: PositionChoice[] } {
  if (value === undefined || value === null) return { problems: [], positions: [] };
  if (!Array.isArray(value)) return { problems: [at('positions must be a list, one per placeholder the email decides.')], positions: [] };
  const problems: BlockProblem[] = [];
  const positions: PositionChoice[] = [];
  const seen = new Set<number>();
  for (const raw of value) {
    const p = asRecord(raw) ?? {};
    const position = Number(p.position);
    if (!Number.isInteger(position) || position < 0) {
      problems.push(at(`A position is a placeholder's number, 0 upwards; "${String(p.position)}" is not one.`));
      continue;
    }
    if (seen.has(position)) {
      problems.push(at(`Position ${position} is decided twice.`));
      continue;
    }
    seen.add(position);
    const mode = p.mode === undefined ? 'fixed' : p.mode;
    if (!(POSITION_MODES as readonly unknown[]).includes(mode)) {
      problems.push(at(`Position ${position}: mode is fixed or varies.`));
      continue;
    }
    const blockObjectId = p.blockObjectId === undefined || p.blockObjectId === null || p.blockObjectId === '' ? null : Number(p.blockObjectId);
    if (mode === 'fixed' && (blockObjectId === null || !Number.isInteger(blockObjectId) || blockObjectId <= 0)) {
      problems.push(at(`Position ${position} is fixed but names no block.`));
      continue;
    }
    const rawIds = p.candidateObjectIds === undefined || p.candidateObjectIds === null ? [] : p.candidateObjectIds;
    if (!Array.isArray(rawIds)) {
      problems.push(at(`Position ${position}: candidateObjectIds must be a list of block ids.`));
      continue;
    }
    const candidateObjectIds = [...new Set(rawIds.map((v) => Number(v)))];
    if (candidateObjectIds.some((n) => !Number.isInteger(n) || n <= 0)) {
      problems.push(at(`Position ${position}: every candidate is a block id.`));
      continue;
    }
    positions.push({
      position,
      mode: mode as PositionMode,
      blockObjectId: mode === 'fixed' ? blockObjectId : null,
      candidateObjectIds: mode === 'varies' ? candidateObjectIds.sort((a, b) => a - b) : [],
    });
  }
  positions.sort((a, b) => a.position - b.position);
  return { problems, positions };
}

/** The part of a template slot a position choice depends on. */
export interface SlotShape {
  position: number;
  kind: string;
  poolObjectId: number | null;
}

/**
 * A draft's choices carried onto a newer version of its template. A campaign
 * pins a template VERSION, and approving the next version supersedes it — after
 * which the draft could neither be saved (the API wants an approved version)
 * nor opened (the editor lists only approved ones), yet could still be
 * submitted. A choice survives when the new version still has a block
 * placeholder at that position drawing from the same type; the rest are
 * dropped and reported, so the editor can say what the marketer must pick again.
 */
export function rebasePositions(
  newSlots: readonly SlotShape[],
  oldSlots: readonly SlotShape[],
  positions: readonly PositionChoice[],
): { positions: PositionChoice[]; dropped: number[] } {
  const kept: PositionChoice[] = [];
  const dropped: number[] = [];
  for (const choice of positions) {
    const before = oldSlots.find((s) => s.position === choice.position);
    const after = newSlots.find((s) => s.position === choice.position);
    if (after && after.kind === 'content_block' && before && before.poolObjectId === after.poolObjectId) kept.push(choice);
    else dropped.push(choice.position);
  }
  return { positions: kept, dropped };
}

// ---- Assembly of a layout ---------------------------------------------------

function escapeAttr(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}

function escapeText(value: string): string {
  return value.replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[ch]!);
}

/**
 * How an article renders when a slot selects it: a section with the title
 * as a link and the summary beneath. Fixed, and inside the allowlist, so an
 * article — which is a URL and some prose, never markup — cannot smuggle
 * anything a block could not.
 */
export function articleMjml(article: { title: string; url: string; summary: string | null }): string {
  const summary = article.summary ? `<p>${escapeText(article.summary)}</p>` : '';
  return `<mj-section><mj-column><mj-text><h2><a href="${escapeAttr(article.url)}">${escapeText(article.title)}</a></h2>${summary}</mj-text></mj-column></mj-section>`;
}

/**
 * The layout with its slots filled — the body of the document assembly
 * compiles (M3). Static sections verbatim; each slot replaced by its
 * selections in order. Positions with no selection are reported so the
 * caller can hold them against `min_items`; a slot allowed to be empty
 * simply disappears.
 */
export function assembleLayout(
  layout: TemplateLayout,
  selections: ReadonlyMap<number, readonly string[]>,
): { mjml: string; empty: number[] } {
  const parts: string[] = [];
  const empty: number[] = [];
  for (const section of layout.sections) {
    if (section.type === 'static') {
      parts.push(section.mjml.trim());
      continue;
    }
    const chosen = selections.get(section.position) ?? [];
    if (chosen.length === 0) empty.push(section.position);
    for (const item of chosen) parts.push(item.trim());
  }
  return { mjml: parts.join('\n'), empty };
}
